/**
 * Ochiq geoportal — interaktiv xarita (ЧТЗ 4.2.11.2 va kengaytirilgan talablar).
 *
 * Faqat ko'rish uchun: ro'yxatdan o'tmasdan va kabinetga kirmasdan ochiq
 * qatlamlarni ko'rish, obyektni tanlash, atributlar, filtr va qidiruv.
 * Tahrirlash, chegarani o'zgartirish va tasdiqlash — xodimlar kabinetida.
 *
 *   * Qatlamlar bo'limlari (yig'iladigan): O'rmon fondidan foydalanish (ijara,
 *     ruxsatnoma, dorivor o'simliklar — holatlari alohida), Hodisalar va yong'in
 *     xavfi, Ko'chatxonalar, Vegetatsiya indekslari (yagona bo'lim), O'rmonni
 *     tiklash va yashil hududlar, qo'shimcha qatlamlar.
 *   * Bog'langan filtrlar: viloyat → tuman → o'rmon xo'jaligi; yo'nalish →
 *     maqsad/tur va holat; hujjat raqami; sana oralig'i; umumiy qidiruv.
 *   * Natijalar ro'yxati xarita bilan sinxron: ro'yxatda bosilsa — xarita
 *     yaqinlashadi, chegara ajratiladi va kartochka ochiladi; ustma-ust
 *     obyektlarda tanlash ro'yxati chiqadi.
 *   * Kartochka: faqat shu turdagi atributlar, bo'shlarida «Ma'lumot mavjud
 *     emas», holat matn bilan, manba va yangilangan sana, amallar (ruxsatnoma,
 *     auksion loti, ijara xizmati, shu joy haqida murojaat).
 *   * Telefonda qatlamlar, filtrlar, natijalar va kartochka pastdan ochiladigan
 *     panellarda; panel yopilganda tanlovlar saqlanadi.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { portalApi } from "@/shared/api/endpoints";
import type {
  GeoAttribute,
  GeoJsonCollection,
  GeoportalObject,
  GeoportalObjects,
  GeoportalOptions,
  PublicMapLayer,
} from "@/shared/api/types";
import { publicBasemapFallback, regionsFallback } from "@/shared/data/landing";
import { translate } from "@/shared/data/portal";
import { findService } from "@/shared/data/serviceCatalog";
import { useI18n } from "@/shared/i18n";
import { formatDate } from "@/shared/lib/format";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";
import { MapView } from "@/shared/ui/MapView";
import type { MapBounds, MapFocus, MapPin, MapPoint, MapShape, MapViewport } from "@/shared/ui/MapView";
import { Modal } from "@/shared/ui/Modal";

import { ServiceActionButton, useServiceLauncher } from "./ServiceGateway";
import { SplitHead } from "./SplitHead";
import { usePublicMap, usePublicMapFilters } from "./publicData";

/* ------------------------------------------------------------------ qatlamlar ro'yxati */

type SectionKey = "forest" | "events" | "nurseries" | "vegetation" | "green" | "extra";

interface LayerStyle {
  color: string;
  stroke?: string;
  dash?: number[];
  fill?: number;
}

interface SubLayer {
  code: string;
  label: string;
  /** `geo` — geoportal API; `gis` — `map/layers/<code>/`. */
  kind: "geo" | "gis";
  style: LayerStyle;
  /** Ichki guruh sarlavhasi (masalan «Ijara»). */
  group?: string;
  /** GIS so'rovining qo'shimcha parametrlari. */
  params?: Record<string, string>;
  source?: string;
  /** Faqat holat filtri bilan ko'rinadi. */
  filterOnly?: boolean;
}

interface Section {
  key: SectionKey;
  title: string;
  icon: IconName;
  layers: SubLayer[];
  /** Bir vaqtda faqat bitta qatlam (vegetatsiya: xo'jalik / tuman kesimi). */
  exclusive?: boolean;
}

const SECTIONS: Section[] = [
  {
    key: "forest",
    title: "O'rmon fondidan foydalanish",
    icon: "tree",
    layers: [
      { code: "forest_fund", label: "Umumiy o'rmon fondi chegaralari", kind: "geo",
        style: { color: "#16a34a", stroke: "#15803d", fill: 0.1 } },
      { code: "lease_leased", group: "Ijara yer maydonlari", label: "Ijaraga berilgan", kind: "geo",
        style: { color: "#2563eb", fill: 0.3 } },
      { code: "lease_auction", group: "Ijara yer maydonlari", label: "Savdoga chiqarilgan", kind: "geo",
        style: { color: "#f59e0b", fill: 0.35 } },
      { code: "lease_planned", group: "Ijara yer maydonlari", label: "Ijaraga chiqarish rejalashtirilgan", kind: "geo",
        style: { color: "#2563eb", dash: [3, 2], fill: 0.12 } },
      { code: "permit_offered", group: "Ruxsatnoma asosida foydalanish", label: "Taklif etilayotgan", kind: "geo",
        style: { color: "#a855f7", dash: [3, 2], fill: 0.15 } },
      { code: "permit_issued", group: "Ruxsatnoma asosida foydalanish", label: "Ruxsatnoma berilgan", kind: "geo",
        style: { color: "#7c3aed", fill: 0.3 } },
      { code: "permit_expired", group: "Ruxsatnoma asosida foydalanish", label: "Muddati tugagan / bekor qilingan",
        kind: "geo", style: { color: "#9ca3af", dash: [1, 2], fill: 0.1 }, filterOnly: true },
      { code: "medicinal_permit", group: "Dorivor o'simliklarni terish", label: "Ruxsatnoma berilgan", kind: "geo",
        style: { color: "#0d9488", fill: 0.3 } },
      { code: "medicinal_auction", group: "Dorivor o'simliklarni terish", label: "Auksionga chiqarilgan", kind: "geo",
        style: { color: "#0891b2", dash: [4, 2], fill: 0.2 } },
    ],
  },
  {
    key: "events",
    title: "Hodisalar va yong'in xavfi",
    icon: "flame",
    layers: [
      { code: "fire_risk", label: "Tasdiqlangan hodisalar (yong'in, noqonuniy kesish va b.)", kind: "gis",
        params: { event_type: "*" }, style: { color: "#dc2626" }, source: "Kuzatuv tizimi (ochiq e'lon qilishga tasdiqlangan)" },
      { code: "firms_fires", label: "Yong'in o'choqlari — sun'iy yo'ldosh (7 kun)", kind: "gis",
        style: { color: "#f97316" }, source: "Sun'iy yo'ldosh termal kuzatuvi" },
    ],
  },
  {
    key: "nurseries",
    title: "Ko'chatxonalar",
    icon: "package",
    layers: [{ code: "nurseries", label: "Ko'chatxonalar", kind: "gis", style: { color: "#0f766e", fill: 0.35 },
      source: "Smart Forestry GIS" }],
  },
  {
    key: "vegetation",
    title: "Vegetatsiya indekslari",
    icon: "leaf",
    exclusive: true,
    layers: [
      { code: "vegetation_index", label: "NDVI — o'rmon xo'jaliklari kesimida", kind: "gis",
        style: { color: "#65a30d", fill: 0.55 }, source: "O'zbekkosmos (SpaceGov Monitoring)" },
      { code: "ndvi_districts", label: "NDVI — tumanlar kesimida", kind: "gis",
        style: { color: "#65a30d", fill: 0.55 }, source: "Sun'iy yo'ldosh ma'lumotlari (tumanlar kesimida)" },
    ],
  },
  {
    key: "green",
    title: "O'rmonni tiklash va yashil hududlar",
    icon: "sparkles",
    layers: [{ code: "afforestation", label: "O'rmon barpo qilish va «Yashil makon» maydonlari", kind: "geo",
      style: { color: "#65a30d", fill: 0.3 } }],
  },
];

/** Ochiq xaritada o'rniga yangi bo'limlar ishlatiladigan eski qatlamlar. */
const REPLACED = new Set(["lease_plots", "auction_plots"]);
const KNOWN = new Set(SECTIONS.flatMap((section) => section.layers.map((layer) => layer.code)));
const GIS_LIMIT = 1500;

/** NDVI shkalasi (backend `ndvi_color` bilan bir xil). */
const NDVI_SCALE = [
  { max: 0.2, color: "#a16207", label: "< 0,2 — siyrak" },
  { max: 0.4, color: "#ca8a04", label: "0,2–0,4" },
  { max: 0.6, color: "#65a30d", label: "0,4–0,6" },
  { max: 2, color: "#15803d", label: "> 0,6 — zich" },
];
const NO_DATA_COLOR = "#cbd5e1";

function ndviColor(value: number): string {
  return (NDVI_SCALE.find((step) => value < step.max) ?? NDVI_SCALE[NDVI_SCALE.length - 1]).color;
}

/** GIS qatlam atributlari yorliqlari (ochiq maydonlar). */
const GIS_LABELS: Record<string, string> = {
  region: "Hudud",
  forestry: "O'rmon xo'jaligi",
  area_ha: "Maydon",
  contacts: "Aloqa",
  status: "Joriy holat",
  severity: "Jiddiylik",
  detected_at: "Aniqlangan sana",
  ndvi: "NDVI qiymati",
  ndvi_class: "Daraja",
  observed_on: "Kuzatuv sanasi",
};

/** GIS qatlamlarida kartochkada majburiy ko'rsatiladigan maydonlar (bo'lsa ham, bo'lmasa ham). */
const GIS_REQUIRED: Record<string, string[]> = {
  fire_risk: ["region", "detected_at", "status"],
  firms_fires: ["detected_at"],
  nurseries: ["forestry", "area_ha", "contacts"],
  vegetation_index: ["ndvi", "observed_on"],
  ndvi_districts: ["ndvi", "ndvi_class", "observed_on"],
};

/* ------------------------------------------------------------------ yagona obyekt */

interface Item {
  uid: string;
  layer: string;
  section: SectionKey;
  title: string;
  status: string;
  attributes: GeoAttribute[];
  actions: GeoportalObject["actions"];
  plotRef: string;
  source: string;
  updated: string | null;
  geometry: { type: string; coordinates: unknown } | null;
  bbox: MapBounds | null;
  center: { lat: number; lon: number } | null;
  /** Poligon bo'lmasa (nuqta) — `true`. */
  point: boolean;
  /** Bosish orqali tanlashda tartib: kichik maydon birinchi. */
  size: number;
  color?: string;
  direction?: string;
}

function polygonsOf(geometry: Item["geometry"]): Array<Array<Array<[number, number]>>> {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return [geometry.coordinates as Array<Array<[number, number]>>];
  if (geometry.type === "MultiPolygon") return geometry.coordinates as Array<Array<Array<[number, number]>>>;
  return [];
}

function boundsOf(geometry: Item["geometry"]): MapBounds | null {
  if (!geometry) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const visit = (pair: [number, number]) => {
    west = Math.min(west, pair[0]);
    east = Math.max(east, pair[0]);
    south = Math.min(south, pair[1]);
    north = Math.max(north, pair[1]);
  };
  if (geometry.type === "Point") visit(geometry.coordinates as [number, number]);
  for (const polygon of polygonsOf(geometry)) for (const ring of polygon) ring.forEach(visit);
  return Number.isFinite(west) ? { west, south, east, north } : null;
}

function insideRings(lon: number, lat: number, rings: Array<Array<[number, number]>>): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi || 1e-12) + xi) inside = !inside;
    }
  }
  return inside;
}

function fromGeo(object: GeoportalObject): Item {
  const bbox = object.bbox
    ? { west: object.bbox[0], south: object.bbox[1], east: object.bbox[2], north: object.bbox[3] }
    : boundsOf(object.geometry);
  return {
    uid: object.uid,
    layer: object.layer,
    section: object.layer === "afforestation" ? "green" : "forest",
    title: object.title,
    status: object.status,
    attributes: object.attributes,
    actions: object.actions,
    plotRef: object.plot_ref,
    source: object.source,
    updated: object.updated,
    geometry: object.geometry,
    bbox,
    center: bbox ? { lat: (bbox.south + bbox.north) / 2, lon: (bbox.west + bbox.east) / 2 } : null,
    point: false,
    size: bbox ? (bbox.east - bbox.west) * (bbox.north - bbox.south) : 0,
    direction: object.direction,
  };
}

function fromGis(feature: GeoJsonCollection["features"][number], sub: SubLayer, section: SectionKey): Item {
  const properties = (feature.properties ?? {}) as Record<string, unknown>;
  const geometry = feature.geometry as Item["geometry"];
  const bbox = boundsOf(geometry);
  const keys = Array.from(new Set([...(GIS_REQUIRED[sub.code] ?? []), ...Object.keys(GIS_LABELS)]));
  const attributes: GeoAttribute[] = keys
    .filter((key) => key in GIS_LABELS && (key in properties || GIS_REQUIRED[sub.code]?.includes(key)))
    .map((key) => {
      const raw = properties[key];
      const empty = raw === undefined || raw === null || raw === "";
      return {
        label: GIS_LABELS[key],
        value: empty ? null : key === "ndvi" ? Number(raw).toFixed(2) : (raw as string | number),
        kind: key === "area_ha" ? "ha" : key.endsWith("_on") || key.endsWith("_at") ? "date" : "text",
      };
    });
  const ndvi = typeof properties.ndvi === "number" ? properties.ndvi : null;
  return {
    uid: `${sub.code}:${String(feature.id)}`,
    layer: sub.code,
    section,
    title: String(properties.name || sub.label),
    status: String(properties.status || (ndvi !== null ? String(properties.ndvi_class || "") : "") || ""),
    attributes,
    actions: ["appeal"],
    plotRef: "",
    source: sub.source ?? "Smart Forestry GIS",
    updated: String(properties.observed_on || properties.detected_at || "") || null,
    geometry,
    bbox,
    center: bbox ? { lat: (bbox.south + bbox.north) / 2, lon: (bbox.west + bbox.east) / 2 } : null,
    point: geometry?.type === "Point",
    size: bbox ? (bbox.east - bbox.west) * (bbox.north - bbox.south) : 0,
    color: ndvi !== null ? ndviColor(ndvi) : typeof properties.color === "string" ? properties.color : undefined,
  };
}

/* ------------------------------------------------------------------ filtrlar */

interface Filters {
  region: string;
  district: string;
  forestry: string;
  direction: string;
  purpose: string;
  status: string;
  doc: string;
  q: string;
  dateFrom: string;
  dateTo: string;
}

const NO_FILTERS: Filters = {
  region: "", district: "", forestry: "", direction: "", purpose: "", status: "",
  doc: "", q: "", dateFrom: "", dateTo: "",
};

/** Xizmat sahifasidan kelganda (`?xizmat=`) — mos yo'nalish va maqsad. */
const SERVICE_DIRECTION: Record<string, string> = { ruxsatnoma: "permit", osimliklar: "medicinal", ijara: "lease" };
const SERVICE_PURPOSE: Record<string, string> = { yaylov: "grazing", pichanzor: "hay", asalari: "beekeeping", tadbir: "events" };

/* ------------------------------------------------------------------ yordamchi UI */

function formatValue(attribute: GeoAttribute, locale: string): string {
  if (attribute.value === null || attribute.value === undefined || attribute.value === "") return "Ma'lumot mavjud emas";
  if (attribute.kind === "ha") return `${Number(attribute.value).toLocaleString("ru-RU")} ga`;
  if (attribute.kind === "date") {
    const text = String(attribute.value);
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? formatDate(text, locale) : text;
  }
  return String(attribute.value);
}

function Swatch({ style }: { style: LayerStyle }) {
  return (
    <span
      className="geo-swatch"
      aria-hidden="true"
      style={{
        background: style.color,
        borderColor: style.stroke ?? style.color,
        borderStyle: style.dash ? "dashed" : "solid",
        opacity: style.dash ? 0.7 : 1,
      }}
    />
  );
}

function Panel({ id, title, open, onClose, children }: {
  id: string; title: string; open: boolean; onClose: () => void; children: ReactNode;
}) {
  return (
    <section className={`geo-panel${open ? " is-open" : ""}`} id={id} aria-label={title}>
      <header className="geo-panel-head">
        <h3>{title}</h3>
        <button type="button" className="icon-btn geo-panel-close" onClick={onClose} aria-label="Panelni yopish">
          <Icon name="close" size={16} />
        </button>
      </header>
      <div className="geo-panel-body">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ asosiy komponent */

type PanelKey = "layers" | "filters" | "results";

/** Telefon ko'rinishi: panellar pastdan ochiladi (bir xil boshqaruvlar ikki joyda chizilmaydi). */
function useCompact(query = "(max-width: 900px)"): boolean {
  const [match, setMatch] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const onChange = () => setMatch(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return match;
}

export function PublicGeoportal() {
  const { t, lang, locale } = useI18n();
  const config = usePublicMap();
  const regionFilters = usePublicMapFilters();
  const launcher = useServiceLauncher();
  const [params, setParams] = useSearchParams();
  const service = findService(params.get("xizmat") ?? "");
  const stage = useRef<HTMLDivElement>(null);
  const compact = useCompact();

  /* qatlamlar va bo'limlar */
  const [active, setActive] = useState<Set<string>>(() => new Set(["forest_fund"]));
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(() => new Set(["forest"]));
  const [opacity, setOpacity] = useState<Record<SectionKey, number>>({
    forest: 1, events: 1, nurseries: 1, vegetation: 1, green: 1, extra: 1,
  });

  /* filtrlar */
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [draftQ, setDraftQ] = useState("");
  const [draftDoc, setDraftDoc] = useState("");
  const [options, setOptions] = useState<GeoportalOptions | null>(null);

  /* ma'lumot */
  const [geo, setGeo] = useState<GeoportalObjects | null>(null);
  const [gis, setGis] = useState<Record<string, GeoJsonCollection>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  /* xarita holati */
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [choices, setChoices] = useState<Item[] | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [fitTarget, setFitTarget] = useState<{ bounds: MapBounds; key: number } | null>(null);
  const [viewport, setViewport] = useState<MapViewport | null>(null);
  const [me, setMe] = useState<MapPin | null>(null);
  const [note, setNote] = useState("");
  const [panel, setPanel] = useState<PanelKey | null>(null);
  const [desktopTab, setDesktopTab] = useState<PanelKey>("layers");
  const [fullscreen, setFullscreen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  /* ---------------------------------------------------------- ma'lumotnomalar */
  useEffect(() => {
    let cancelled = false;
    portalApi
      .geoportalOptions()
      .then((data) => !cancelled && setOptions(data))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /* Xizmat sahifasidan kelganda: tegishli yo'nalish va qatlamlar. */
  const serviceCode = service?.code ?? "";
  const purposeParam = params.get("maqsad") ?? "";
  useEffect(() => {
    const direction = SERVICE_DIRECTION[serviceCode];
    if (!direction) return;
    setFilters((previous) => ({ ...previous, direction, purpose: SERVICE_PURPOSE[purposeParam] ?? "", status: "" }));
    setActive((previous) => {
      const next = new Set(previous);
      SECTIONS[0].layers.filter((layer) => !layer.filterOnly).forEach((layer) => next.add(layer.code));
      return next;
    });
    setOpenSections((previous) => new Set(previous).add("forest"));
  }, [serviceCode, purposeParam]);

  /* ---------------------------------------------------------- geoportal obyektlari */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    portalApi
      .geoportalObjects(
        {
          region: filters.region, district: filters.district, forestry: filters.forestry,
          direction: filters.direction, purpose: filters.purpose, status: filters.status,
          doc: filters.doc, q: filters.q, date_from: filters.dateFrom, date_to: filters.dateTo,
        },
        controller.signal,
      )
      .then((data) => {
        setGeo(data);
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadError("Ma'lumotni yuklab bo'lmadi. Internet aloqasini tekshirib, qayta urinib ko'ring.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [filters]);

  /* Holat filtri tanlansa, o'sha qatlam avtomatik yoqiladi (masalan bekor qilinganlar). */
  useEffect(() => {
    if (!filters.status) return;
    setActive((previous) => new Set(previous).add(filters.status));
  }, [filters.status]);

  /* ---------------------------------------------------------- GIS qatlamlari */
  const extraLayers: SubLayer[] = useMemo(
    () =>
      (config.data?.layers ?? [])
        .filter((layer: PublicMapLayer) => !KNOWN.has(layer.code) && !REPLACED.has(layer.code))
        .map((layer) => {
          const style = (layer.style ?? {}) as Record<string, unknown>;
          const color = typeof style.color === "string" ? style.color : typeof style.fill === "string" ? style.fill : "#64748b";
          return {
            code: layer.code,
            label: translate(layer.name, lang) || layer.name_uz || layer.code,
            kind: "gis" as const,
            style: { color, fill: 0.15 },
            source: "Smart Forestry GIS",
          };
        }),
    [config.data, lang],
  );
  const sections: Section[] = useMemo(
    () => [...SECTIONS, ...(extraLayers.length ? [{ key: "extra" as const, title: "Qo'shimcha qatlamlar", icon: "layers" as IconName, layers: extraLayers }] : [])],
    [extraLayers],
  );
  const subByCode = useMemo(() => {
    const map = new Map<string, { sub: SubLayer; section: Section }>();
    sections.forEach((section) => section.layers.forEach((sub) => map.set(sub.code, { sub, section })));
    return map;
  }, [sections]);

  const needAdminDistricts = active.has("ndvi_districts");
  const gisCodes = useMemo(() => {
    const codes = Array.from(active).filter((code) => subByCode.get(code)?.sub.kind === "gis");
    if (needAdminDistricts && !codes.includes("admin_districts")) codes.push("admin_districts");
    return codes.sort();
  }, [active, subByCode, needAdminDistricts]);

  useEffect(() => {
    const controller = new AbortController();
    const missing = gisCodes;
    if (!missing.length) return undefined;
    Promise.all(
      missing.map((code) =>
        portalApi
          .mapFeatures(
            code,
            {
              ...(subByCode.get(code)?.sub.params ?? {}),
              region: filters.region, district: filters.district, forestry: filters.forestry,
              date_from: filters.dateFrom, date_to: filters.dateTo, limit: GIS_LIMIT,
            },
            controller.signal,
          )
          .then((collection) => [code, collection] as const),
      ),
    )
      .then((entries) => setGis((previous) => ({ ...previous, ...Object.fromEntries(entries) })))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadError("Ayrim qatlamlarni yuklab bo'lmadi — keyinroq qayta urinib ko'ring.");
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gisCodes.join(","), filters.region, filters.district, filters.forestry, filters.dateFrom, filters.dateTo]);

  /* ---------------------------------------------------------- yagona obyektlar ro'yxati */
  const allItems: Item[] = useMemo(() => {
    const items: Item[] = [];
    for (const object of geo?.objects ?? []) {
      if (active.has(object.layer)) items.push(fromGeo(object));
    }
    const text = filters.q.trim().toLowerCase();
    for (const code of gisCodes) {
      const entry = subByCode.get(code);
      if (!entry || !active.has(code)) continue;
      for (const feature of gis[code]?.features ?? []) {
        const item = fromGis(feature, entry.sub, entry.section.key);
        if (text && !item.title.toLowerCase().includes(text)) continue;
        items.push(item);
      }
    }
    /* Ro'yxatda aniq obyektlar birinchi, asosiy qatlam (o'rmon fondi chegaralari) oxirida. */
    return items.sort((a, b) => Number(a.layer === "forest_fund") - Number(b.layer === "forest_fund"));
  }, [geo, gis, gisCodes, active, subByCode, filters.q]);

  const itemByUid = useMemo(() => new Map(allItems.map((item) => [item.uid, item])), [allItems]);
  const selected = selectedUid ? itemByUid.get(selectedUid) ?? null : null;

  const counts = useMemo(() => {
    const result: Record<string, number> = { ...(geo?.counts ?? {}) };
    for (const code of gisCodes) result[code] = gis[code]?.features.length ?? 0;
    return result;
  }, [geo, gis, gisCodes]);

  const inView = useMemo(() => {
    if (!viewport) return allItems.length;
    const view = viewport.bounds;
    return allItems.filter(
      (item) => item.bbox && item.bbox.east >= view.west && item.bbox.west <= view.east
        && item.bbox.north >= view.south && item.bbox.south <= view.north,
    ).length;
  }, [allItems, viewport]);

  /* ---------------------------------------------------------- xaritaga chizish */
  const { shapes, points } = useMemo(() => {
    const nextShapes: MapShape[] = [];
    const nextPoints: MapPoint[] = [];
    /* NDVI tumanlar: ma'lumotsiz tumanlar alohida (kulrang) — past indeks emas. */
    if (active.has("ndvi_districts")) {
      const withData = new Set((gis.ndvi_districts?.features ?? []).map((feature) =>
        String((feature.properties as Record<string, unknown>)?.name ?? "").toLowerCase()));
      for (const feature of gis.admin_districts?.features ?? []) {
        const name = String((feature.properties as Record<string, unknown>)?.name ?? "").toLowerCase();
        if (withData.has(name)) continue;
        const polygons = polygonsOf(feature.geometry as Item["geometry"]);
        if (!polygons.length) continue;
        nextShapes.push({
          id: `nodata:${String(feature.id)}`, rings: polygons.flat(), polygons, color: NO_DATA_COLOR,
          strokeColor: "#94a3b8", fillOpacity: 0.35 * opacity.vegetation, strokeWidth: 0.8, dash: [2, 2],
          title: `${String((feature.properties as Record<string, unknown>)?.name ?? "")} — NDVI ma'lumoti mavjud emas`,
        });
      }
    }
    for (const item of allItems) {
      const entry = subByCode.get(item.layer);
      if (!entry) continue;
      const style = entry.sub.style;
      const factor = opacity[entry.section.key] ?? 1;
      if (item.point && item.center) {
        nextPoints.push({
          id: item.uid, lat: item.center.lat, lon: item.center.lon, color: item.color ?? style.color,
          title: item.title, subtitle: item.status,
        });
        continue;
      }
      const polygons = polygonsOf(item.geometry);
      if (!polygons.length) continue;
      const planned = item.layer === "afforestation" && item.status === "Rejalashtirilgan";
      nextShapes.push({
        id: item.uid,
        rings: polygons.flat(),
        polygons,
        color: item.color ?? style.color,
        strokeColor: style.stroke ?? item.color ?? style.color,
        fillOpacity: (style.fill ?? 0.25) * factor,
        strokeWidth: 1.4,
        dash: planned ? [3, 2] : style.dash,
        title: item.title,
      });
    }
    /* Tanlangan obyekt chegarasi ajratiladi. */
    if (selected && !selected.point) {
      const polygons = polygonsOf(selected.geometry);
      if (polygons.length) {
        nextShapes.push({
          id: `selected:${selected.uid}`, rings: polygons.flat(), polygons, color: "#dc2626",
          strokeColor: "#dc2626", fillOpacity: 0.06, strokeWidth: 3.2, title: selected.title,
        });
      }
    }
    return { shapes: nextShapes, points: nextPoints };
  }, [allItems, subByCode, opacity, selected, active, gis]);

  const pins = useMemo(() => {
    const list: MapPin[] = [];
    if (me) list.push(me);
    if (selected?.point && selected.center) list.push({ ...selected.center, label: selected.title, tone: "danger" });
    return list;
  }, [me, selected]);

  const basemaps = useMemo(() => {
    const list = config.data?.basemaps?.length ? config.data.basemaps : [publicBasemapFallback];
    return list.map((row) => ({ code: row.code, name: row.name, url_template: row.url_template, attribution: row.attribution }));
  }, [config.data]);
  const initial = config.data?.initial_view ?? { lat: 41.3, lon: 64.5, zoom: 6 };

  /* ---------------------------------------------------------- amallar */
  function selectItem(item: Item, fit: boolean) {
    setSelectedUid(item.uid);
    setChoices(null);
    setPanel(null);
    if (fit) {
      if (item.bbox && !item.point) setFitTarget({ bounds: item.bbox, key: Date.now() });
      else if (item.center) setFocus({ ...item.center, zoom: 13, key: `item-${Date.now()}` });
    }
    window.requestAnimationFrame(() =>
      document.getElementById(`geo-row-${item.uid}`)?.scrollIntoView({ block: "nearest" }),
    );
  }

  function onMapClick(point: { lat: number; lon: number }) {
    const hits = allItems
      .filter((item) => !item.point && polygonsOf(item.geometry).some((rings) => insideRings(point.lon, point.lat, rings)))
      .sort((a, b) => a.size - b.size);
    if (hits.length === 0) {
      setSelectedUid(null);
      setChoices(null);
    } else if (hits.length === 1) {
      selectItem(hits[0], false);
    } else {
      setSelectedUid(null);
      setChoices(hits.slice(0, 12));
    }
  }

  function toggleLayer(code: string) {
    setActive((previous) => {
      const next = new Set(previous);
      const entry = subByCode.get(code);
      if (next.has(code)) next.delete(code);
      else {
        if (entry?.section.exclusive) entry.section.layers.forEach((layer) => next.delete(layer.code));
        next.add(code);
      }
      return next;
    });
  }

  function toggleSection(section: Section) {
    const codes = section.layers.filter((layer) => !layer.filterOnly).map((layer) => layer.code);
    const on = codes.some((code) => active.has(code));
    setActive((previous) => {
      const next = new Set(previous);
      if (on) codes.forEach((code) => next.delete(code));
      else (section.exclusive ? codes.slice(0, 1) : codes).forEach((code) => next.add(code));
      return next;
    });
  }

  function setFilter(patch: Partial<Filters>) {
    /* Yo'nalish yoki hujjat bo'yicha qidirilganda tegishli qatlamlar yoqiladi — aks holda
       topilgan obyekt o'chiq qatlamda qolib, «topilmadi» deb ko'rinardi. */
    const forestLayers = SECTIONS[0].layers.filter((layer) => !layer.filterOnly);
    if (patch.direction || patch.doc) {
      const directionPrefix = patch.direction === "medicinal" ? "medicinal_" : patch.direction ? `${patch.direction}_` : "";
      setActive((previous) => {
        const next = new Set(previous);
        forestLayers
          .filter((layer) => !directionPrefix || layer.code.startsWith(directionPrefix))
          .forEach((layer) => next.add(layer.code));
        return next;
      });
      setOpenSections((previous) => new Set(previous).add("forest"));
    }
    setFilters((previous) => {
      const next = { ...previous, ...patch };
      /* Yuqori tanlov o'zgarsa mos kelmaydigan quyi tanlovlar tozalanadi. */
      if ("region" in patch) {
        next.district = "";
        next.forestry = "";
      }
      if ("district" in patch) next.forestry = "";
      if ("direction" in patch) {
        next.purpose = "";
        next.status = "";
      }
      return next;
    });
    setSelectedUid(null);
  }

  function resetFilters() {
    setFilters(NO_FILTERS);
    setDraftQ("");
    setDraftDoc("");
    setSelectedUid(null);
    setChoices(null);
  }

  function search(event: FormEvent) {
    event.preventDefault();
    const text = draftQ.trim();
    const coordinates = text.match(/^(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)$/);
    if (coordinates) {
      const lat = Number(coordinates[1].replace(",", "."));
      const lon = Number(coordinates[2].replace(",", "."));
      setFocus({ lat, lon, zoom: 13, key: `coords-${Date.now()}` });
      setMe({ lat, lon, label: `${lat.toFixed(4)}, ${lon.toFixed(4)}`, tone: "info" });
      setNote(`Koordinata: ${lat.toFixed(4)}, ${lon.toFixed(4)}`);
      return;
    }
    const needle = text.toLowerCase();
    const region = regionsList.find((item) => item.name.toLowerCase().includes(needle));
    if (needle && region) {
      setFocus({ lat: region.lat, lon: region.lon, zoom: region.zoom, key: `region-${Date.now()}` });
      setNote(`Hudud: ${region.name}`);
    } else setNote("");
    setFilters((previous) => ({ ...previous, q: text }));
  }

  function locate() {
    if (!("geolocation" in navigator)) {
      setNote("Brauzeringiz joylashuvni aniqlashni qo'llab-quvvatlamaydi — xaritadan qo'lda foydalaning.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setMe({ lat, lon, label: "Siz shu yerdasiz", tone: "info" });
        setFocus({ lat, lon, zoom: 13, key: `me-${Date.now()}` });
        setNote("Joylashuvingiz aniqlandi.");
      },
      () => setNote("Joylashuvni aniqlashga ruxsat berilmadi — xaritadan qo'lda foydalanishingiz mumkin."),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function resetView() {
    setFocus({ lat: initial.lat, lon: initial.lon, zoom: initial.zoom, key: `home-${Date.now()}` });
  }

  function toggleFullscreen() {
    const node = stage.current?.closest(".geo") as HTMLElement | null;
    if (!fullscreen && node?.requestFullscreen) {
      node.requestFullscreen().catch(() => undefined);
    } else if (fullscreen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    }
    setFullscreen((value) => !value);
  }
  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  /* Hudud tanlanganda — natijalar chegarasiga yaqinlashish. */
  const placeKey = `${filters.region}|${filters.district}|${filters.forestry}|${filters.doc}|${filters.q}`;
  const lastPlace = useRef(placeKey);
  useEffect(() => {
    if (lastPlace.current === placeKey || loading) return;
    lastPlace.current = placeKey;
    /* Aniq obyektlar bo'lsa — faqat ular bo'yicha (o'rmon fondi chegaralari butun respublikani qamraydi). */
    const specific = allItems.filter((item) => item.layer !== "forest_fund");
    const boxes = (specific.length ? specific : allItems).map((item) => item.bbox).filter((box): box is MapBounds => Boolean(box));
    if (!boxes.length) return;
    setFitTarget({
      bounds: {
        west: Math.min(...boxes.map((box) => box.west)), south: Math.min(...boxes.map((box) => box.south)),
        east: Math.max(...boxes.map((box) => box.east)), north: Math.max(...boxes.map((box) => box.north)),
      },
      key: Date.now(),
    });
  }, [placeKey, loading, allItems]);

  /* ---------------------------------------------------------- ma'lumotnoma ro'yxatlari */
  const regionsList = useMemo(() => {
    const list = regionFilters.data?.regions;
    if (!list?.length) return regionsFallback;
    return list
      .filter((item) => item.lat !== null && item.lon !== null)
      .map((item) => ({
        code: item.code, name: translate(item.name, lang), lat: item.lat as number, lon: item.lon as number,
        zoom: regionsFallback.find((row) => row.code === item.code)?.zoom ?? 8,
      }));
  }, [regionFilters.data, lang]);
  const regionOptions = options?.regions ?? regionsList.map((item) => ({ code: item.code, name: item.name }));
  const districtOptions = (options?.districts ?? []).filter((item) => !filters.region || item.region === filters.region);
  const forestryOptions = (options?.forestries ?? []).filter(
    (item) => (!filters.region || item.region === filters.region)
      && (!filters.district || String(item.district ?? "") === filters.district),
  );
  const purposeOptions = filters.direction ? options?.purposes[filters.direction] ?? [] : [];
  const statusOptions = filters.direction ? options?.statuses[filters.direction] ?? [] : [];
  const filtered = Object.entries(filters).some(([, value]) => Boolean(value));

  const leaseSource = geo?.sources?.lease;
  const integrationStale = Boolean(
    leaseSource?.integration_updated
      && Date.now() - new Date(leaseSource.integration_updated).getTime() > 7 * 86400000,
  );

  /* ---------------------------------------------------------- bo'laklar */
  const layersPanel = (
    <div className="geo-layers">
      {sections.map((section) => {
        const open = openSections.has(section.key);
        const visible = section.layers.filter((layer) => !layer.filterOnly || filters.status === layer.code);
        const anyOn = visible.some((layer) => active.has(layer.code));
        const groups = Array.from(new Set(visible.map((layer) => layer.group ?? "")));
        return (
          <div className={`geo-section${open ? " is-open" : ""}`} key={section.key}>
            <div className="geo-section-head">
              <input
                type="checkbox"
                checked={anyOn}
                onChange={() => toggleSection(section)}
                aria-label={`${section.title} — barcha qatlamlarni yoqish yoki o'chirish`}
              />
              <button
                type="button"
                className="geo-section-toggle"
                aria-expanded={open}
                onClick={() =>
                  setOpenSections((previous) => {
                    const next = new Set(previous);
                    if (next.has(section.key)) next.delete(section.key);
                    else next.add(section.key);
                    return next;
                  })
                }
              >
                <Icon name={section.icon} size={16} />
                <span>{section.title}</span>
                <Icon name="chevronDown" size={14} className={open ? "flip-y" : undefined} />
              </button>
            </div>
            {open ? (
              <div className="geo-section-body">
                {groups.map((group) => (
                  <div className="geo-subgroup" key={group || "main"}>
                    {group ? <span className="geo-subgroup-title">{group}</span> : null}
                    {visible.filter((layer) => (layer.group ?? "") === group).map((layer) => (
                      <label className="geo-layer" key={layer.code}>
                        <input
                          type={section.exclusive ? "radio" : "checkbox"}
                          name={section.exclusive ? `geo-${section.key}` : undefined}
                          checked={active.has(layer.code)}
                          onChange={() => toggleLayer(layer.code)}
                          onClick={() => {
                            if (section.exclusive && active.has(layer.code)) toggleLayer(layer.code);
                          }}
                        />
                        <Swatch style={layer.style} />
                        <span className="geo-layer-name">{layer.label}</span>
                        {active.has(layer.code) && counts[layer.code] !== undefined ? (
                          <small className="num" title="Filtrga mos obyektlar soni">{counts[layer.code]}</small>
                        ) : null}
                      </label>
                    ))}
                  </div>
                ))}
                {section.key === "vegetation" && anyOn ? (
                  <div className="geo-legend" aria-label="NDVI rang shkalasi">
                    {NDVI_SCALE.map((step) => (
                      <span key={step.label}>
                        <i style={{ background: step.color }} />
                        {step.label}
                      </span>
                    ))}
                    <span>
                      <i className="is-nodata" style={{ background: NO_DATA_COLOR }} />
                      Ma'lumot mavjud emas (past indeks emas)
                    </span>
                  </div>
                ) : null}
                {anyOn && section.key !== "events" ? (
                  <label className="geo-opacity">
                    <span>Shaffoflik</span>
                    <input
                      type="range"
                      min={0.2}
                      max={1}
                      step={0.1}
                      value={opacity[section.key]}
                      onChange={(event) => setOpacity((previous) => ({ ...previous, [section.key]: Number(event.target.value) }))}
                      aria-label={`${section.title} — qatlam shaffofligi`}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const filtersPanel = (
    <div className="geo-filters">
      <span className="field">
        <label className="label" htmlFor="geo-region">Viloyat</label>
        <select id="geo-region" className="input select" value={filters.region} onChange={(event) => setFilter({ region: event.target.value })}>
          <option value="">Barcha viloyatlar</option>
          {regionOptions.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </span>
      <span className="field">
        <label className="label" htmlFor="geo-district">Tuman</label>
        <select id="geo-district" className="input select" value={filters.district} disabled={!filters.region || !districtOptions.length}
          onChange={(event) => setFilter({ district: event.target.value })}>
          <option value="">{filters.region ? "Barcha tumanlar" : "Avval viloyatni tanlang"}</option>
          {districtOptions.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
        </select>
      </span>
      <span className="field">
        <label className="label" htmlFor="geo-forestry">O'rmon xo'jaligi</label>
        <select id="geo-forestry" className="input select" value={filters.forestry} onChange={(event) => setFilter({ forestry: event.target.value })}>
          <option value="">Barcha xo'jaliklar</option>
          {forestryOptions.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
        </select>
      </span>
      <span className="field">
        <label className="label" htmlFor="geo-direction">Foydalanish yo'nalishi</label>
        <select id="geo-direction" className="input select" value={filters.direction} onChange={(event) => setFilter({ direction: event.target.value })}>
          <option value="">Barcha yo'nalishlar</option>
          {(options?.directions ?? []).map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </span>
      <span className="field">
        <label className="label" htmlFor="geo-purpose">Maqsad yoki tur</label>
        <select id="geo-purpose" className="input select" value={filters.purpose} disabled={!purposeOptions.length}
          onChange={(event) => setFilter({ purpose: event.target.value })}>
          <option value="">{filters.direction ? "Barchasi" : "Avval yo'nalishni tanlang"}</option>
          {purposeOptions.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </span>
      <span className="field">
        <label className="label" htmlFor="geo-status">Holat</label>
        <select id="geo-status" className="input select" value={filters.status} disabled={!statusOptions.length}
          onChange={(event) => setFilter({ status: event.target.value })}>
          <option value="">{filters.direction ? "Barcha faol holatlar" : "Avval yo'nalishni tanlang"}</option>
          {statusOptions.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </select>
      </span>
      <form className="field" onSubmit={(event) => { event.preventDefault(); setFilter({ doc: draftDoc.trim() }); }}>
        <label className="label" htmlFor="geo-doc">Hujjat raqami</label>
        <span className="geo-inline">
          <input id="geo-doc" className="input" value={draftDoc} onChange={(event) => setDraftDoc(event.target.value)}
            placeholder="Shartnoma, ariza, ruxsatnoma yoki lot" maxLength={64} />
          <button type="submit" className="btn btn-outline btn-sm">Izlash</button>
        </span>
      </form>
      <div className="geo-dates">
        <span className="field">
          <label className="label" htmlFor="geo-from">Sanadan</label>
          <input id="geo-from" className="input" type="date" value={filters.dateFrom} max={filters.dateTo || undefined}
            onChange={(event) => setFilter({ dateFrom: event.target.value })} />
        </span>
        <span className="field">
          <label className="label" htmlFor="geo-to">Sanagacha</label>
          <input id="geo-to" className="input" type="date" value={filters.dateTo} min={filters.dateFrom || undefined}
            onChange={(event) => setFilter({ dateTo: event.target.value })} />
        </span>
      </div>
      <p className="hint">Sana: hodisa sanasi yoki hujjatning amal qilish davri. Yo'nalish, holat va hujjat filtrlari «O'rmon fondidan foydalanish» bo'limiga qo'llanadi.</p>
      <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters} disabled={!filtered}>
        <Icon name="refresh" size={14} />
        Filtrlarni tozalash
      </button>
    </div>
  );

  const resultsPanel = (
    <div className="geo-results">
      <p className="geo-results-count" role="status" aria-live="polite">
        {loading ? "Yuklanmoqda…" : (
          <>
            Jami: <b className="num">{allItems.length}</b> · Joriy ko'rinishda: <b className="num">{inView}</b>
          </>
        )}
      </p>
      {!loading && allItems.length === 0 ? (
        <div className="geo-empty">
          <Icon name="search" size={22} />
          <p>Tanlangan shartlar bo'yicha obyekt topilmadi.</p>
          {filtered ? <button type="button" className="btn btn-outline btn-sm" onClick={resetFilters}>Filtrlarni tozalash</button> : null}
        </div>
      ) : (
        <ul className="geo-result-list">
          {allItems.slice(0, 300).map((item) => {
            const entry = subByCode.get(item.layer);
            return (
              <li key={item.uid} id={`geo-row-${item.uid}`}>
                <button type="button" className={`geo-result${selectedUid === item.uid ? " is-selected" : ""}`}
                  onClick={() => selectItem(item, true)} aria-current={selectedUid === item.uid}>
                  {entry ? <Swatch style={{ ...entry.sub.style, color: item.color ?? entry.sub.style.color }} /> : null}
                  <span>
                    <b>{item.title}</b>
                    <small>{entry?.sub.label}{item.status && item.status !== entry?.sub.label ? ` · ${item.status}` : ""}</small>
                    {!item.geometry ? <small className="geo-nogeom">Chegara ma'lumoti mavjud emas</small> : null}
                  </span>
                </button>
              </li>
            );
          })}
          {allItems.length > 300 ? <li className="hint">Birinchi 300 ta ko'rsatildi — filtr yoki xaritani yaqinlashtiring.</li> : null}
        </ul>
      )}
    </div>
  );

  const actionButton = (item: Item, action: Item["actions"][number]) => {
    if (action === "appeal" && item.center) {
      const query = new URLSearchParams({ lat: item.center.lat.toFixed(6), lon: item.center.lon.toFixed(6), joy: item.title });
      return (
        <Link key={action} className="btn btn-ghost btn-sm" to={`/murojaat?${query.toString()}`}>
          <Icon name="message" size={14} />
          Shu joy haqida murojaat yuborish
        </Link>
      );
    }
    if (action === "lease_service") {
      return (
        <Link key={action} className="btn btn-outline btn-sm" to="/xizmatlar/ijara">
          <Icon name="briefcase" size={14} />
          Ijara xizmatiga o'tish
        </Link>
      );
    }
    if (action === "permit" || action === "auction_lot") {
      const catalog = findService(action === "auction_lot" ? "ijara" : item.direction === "medicinal" ? "osimliklar" : "ruxsatnoma");
      if (!catalog) return null;
      const target = action === "auction_lot" ? "eauksion" : item.direction === "medicinal" ? "licenses" : "ruxsatnoma";
      return (
        <ServiceActionButton
          key={action}
          service={catalog}
          primary
          action={{ kind: "gateway", target, label: action === "auction_lot" ? "Auksion lotini ko'rish" : "Ruxsatnoma olish",
            icon: action === "auction_lot" ? "scale" : "file" }}
          launcher={{
            ...launcher,
            launch: (code, title) => launcher.launch(code, title, {
              plot: item.plotRef || item.title, lat: item.center?.lat.toFixed(6), lon: item.center?.lon.toFixed(6),
            }),
          }}
        />
      );
    }
    return null;
  };

  const card = selected ? (
    <div className="geo-card" role="dialog" aria-label={selected.title}>
      <div className="geo-card-head">
        <div>
          <span className="badge badge-plain">{subByCode.get(selected.layer)?.sub.label}</span>
          <b>{selected.title}</b>
          {selected.status ? <span className="geo-status">Holati: {selected.status}</span> : null}
        </div>
        <button type="button" className="icon-btn" onClick={() => setSelectedUid(null)} aria-label={t("action.close")}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <dl className="geo-attrs">
        {selected.attributes.map((attribute) => (
          <div key={attribute.label} className={attribute.value === null ? "is-empty" : undefined}>
            <dt>{attribute.label}</dt>
            <dd>{formatValue(attribute, locale)}</dd>
          </div>
        ))}
      </dl>
      <p className="geo-source">
        <Icon name="database" size={12} /> Manba: {selected.source}
        {selected.updated ? ` · yangilangan: ${formatDate(selected.updated, locale)}` : ""}
      </p>
      {selected.section === "forest" && selected.layer !== "forest_fund" && integrationStale ? (
        <p className="geo-stale">
          <Icon name="alert" size={12} /> E-AUKSION ma'lumoti {formatDate(leaseSource?.integration_updated ?? "", locale)} holatiga —
          yangilanish kechikmoqda, oxirgi tasdiqlangan ma'lumot ko'rsatilgan.
        </p>
      ) : null}
      <div className="geo-card-actions">{selected.actions.map((action) => actionButton(selected, action))}</div>
      {launcher.error ? <p className="field-error">{launcher.error}</p> : null}
    </div>
  ) : choices ? (
    <div className="geo-card" role="dialog" aria-label="Obyektni tanlang">
      <div className="geo-card-head">
        <div>
          <b>Bu joyda bir nechta obyekt bor</b>
          <span className="geo-status">Qaysi birini ko'rmoqchisiz?</span>
        </div>
        <button type="button" className="icon-btn" onClick={() => setChoices(null)} aria-label={t("action.close")}>
          <Icon name="close" size={16} />
        </button>
      </div>
      <ul className="geo-choices">
        {choices.map((item) => {
          const entry = subByCode.get(item.layer);
          return (
            <li key={item.uid}>
              <button type="button" className="geo-result" onClick={() => selectItem(item, false)}>
                {entry ? <Swatch style={entry.sub.style} /> : null}
                <span>
                  <b>{item.title}</b>
                  <small>{entry?.sub.label}{item.status && item.status !== entry?.sub.label ? ` · ${item.status}` : ""}</small>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  const tabs: Array<{ key: PanelKey; label: string; icon: IconName }> = [
    { key: "layers", label: "Qatlamlar", icon: "layers" },
    { key: "filters", label: filtered ? "Filtrlar •" : "Filtrlar", icon: "filter" },
    { key: "results", label: `Natijalar (${allItems.length})`, icon: "file" },
  ];

  return (
    <section className="section map-section" id="map" aria-labelledby="map-title">
      <div className="container">
        <SplitHead
          scene="map"
          eyebrow={t("nav.map")}
          title="Ochiq geoportal — interaktiv xarita"
          titleId="map-title"
          lead="Ro'yxatdan o'tmasdan ko'rish mumkin. Faqat ochiq e'lon qilishga tasdiqlangan ma'lumotlar ko'rsatiladi."
        >
        </SplitHead>

        {service ? (
          <div className="map-service-banner" data-reveal role="region" aria-label="Xizmat rejimi">
            <span className="gateway-icon" aria-hidden="true">
              <Icon name={service.icon} size={18} />
            </span>
            <div className="map-service-text">
              <b>{service.title}</b>
              <span>Mos maydonlar ko'rsatilgan — maydonni bosing, uning kartochkasidan xizmatga o'tasiz.</span>
            </div>
            <div className="map-service-actions">
              <Link className="btn btn-ghost btn-sm" to={`/xizmatlar/${service.code}`}>Xizmat haqida</Link>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => {
                const next = new URLSearchParams(params);
                next.delete("xizmat");
                next.delete("maqsad");
                setParams(next, { replace: true });
                resetFilters();
              }}>
                <Icon name="close" size={14} />
                Oddiy xarita
              </button>
            </div>
          </div>
        ) : null}

        <div className={`geo${fullscreen ? " is-fullscreen" : ""}`} data-reveal>
          {compact ? null : (
          <aside className="geo-side" aria-label="Xarita boshqaruvi">
            <form className="geo-search" role="search" onSubmit={search}>
              <label className="visually-hidden" htmlFor="geo-q">Obyekt nomi, hudud yoki koordinata bo'yicha qidirish</label>
              <span className="input-group">
                <span className="input-icon"><Icon name="search" size={16} /></span>
                <input id="geo-q" className="input" type="search" placeholder="Obyekt, hudud yoki 41.31, 69.28"
                  value={draftQ} onChange={(event) => setDraftQ(event.target.value)} />
              </span>
              <button type="submit" className="btn btn-primary btn-sm">{t("action.search")}</button>
            </form>
            {note ? <p className="hint" aria-live="polite">{note}</p> : null}
            <div className="segmented geo-tabs" role="tablist">
              {tabs.map((tab) => (
                <button key={tab.key} type="button" role="tab" aria-selected={desktopTab === tab.key}
                  aria-pressed={desktopTab === tab.key} onClick={() => setDesktopTab(tab.key)}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="geo-side-body">
              {desktopTab === "layers" ? layersPanel : desktopTab === "filters" ? filtersPanel : resultsPanel}
            </div>
          </aside>
          )}

          <div className="geo-stage" ref={stage}>
            {compact ? (
            <div className="geo-mobile-bar" role="toolbar" aria-label="Xarita panellari">
              {tabs.map((tab) => (
                <button key={tab.key} type="button" className={`btn btn-sm ${panel === tab.key ? "btn-primary" : "btn-outline"}`}
                  aria-expanded={panel === tab.key} aria-controls={`geo-panel-${tab.key}`}
                  onClick={() => setPanel((current) => (current === tab.key ? null : tab.key))}>
                  <Icon name={tab.icon} size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
            ) : null}
            <MapView
              basemaps={basemaps}
              center={{ lat: initial.lat, lon: initial.lon }}
              zoom={initial.zoom}
              shapes={shapes}
              points={points}
              pins={pins}
              focus={focus}
              fitTarget={fitTarget}
              onMapClick={onMapClick}
              onSelect={(id) => {
                const item = itemByUid.get(id);
                if (item) selectItem(item, false);
              }}
              onViewportChange={setViewport}
              popup={false}
              scaleBar
              fullscreenActive={fullscreen}
              onToggleFullscreen={toggleFullscreen}
              loading={loading && !geo}
              extraTools={
                <>
                  <button type="button" className="map-tool" onClick={resetView} title="Umumiy ko'rinishga qaytish" aria-label="Umumiy ko'rinishga qaytish">
                    <Icon name="home" size={16} />
                  </button>
                  <button type="button" className="map-tool" onClick={locate} title="Mening joylashuvim" aria-label="Mening joylashuvim">
                    <Icon name="target" size={16} />
                  </button>
                </>
              }
            />
            <button type="button" className="geo-help-btn" onClick={() => setHelpOpen(true)}>
              <Icon name="help" size={13} />
              Xaritadan foydalanish
            </button>
            <div className="geo-status-bar" role="status" aria-live="polite">
              {loadError || (loading ? "Ma'lumot yuklanmoqda…" : `${allItems.length} ta obyekt · ko'rinishda ${inView}`)}
            </div>
            {card ? <div className="geo-card-wrap">{card}</div> : null}

            {/* Telefon: pastdan ochiladigan panellar (yopilganda tanlovlar saqlanadi). */}
            {compact && panel ? (
              <Panel id={`geo-panel-${panel}`} title={tabs.find((tab) => tab.key === panel)?.label ?? ""} open onClose={() => setPanel(null)}>
                {panel === "layers" ? layersPanel : panel === "filters" ? filtersPanel : resultsPanel}
              </Panel>
            ) : null}
          </div>
        </div>
      </div>

      <Modal open={helpOpen} title="Xaritadan foydalanish" onClose={() => setHelpOpen(false)} size="md">
        <ol className="geo-help">
          <li><b>Qatlamlar</b> — bo'limni oching va kerakli qatlam yoki holatni yoqing. Bir nechta qatlamni birga ko'rish mumkin; ranglar ma'nosi qatlam yonida.</li>
          <li><b>Filtrlar</b> — viloyat → tuman → o'rmon xo'jaligini, so'ng yo'nalish, maqsad, holat yoki hujjat raqamini tanlang.</li>
          <li><b>Obyektni tanlash</b> — xaritada yoki «Natijalar» ro'yxatida bosing: chegara ajratiladi va kartochka ochiladi. Ustma-ust obyektlarda tanlash ro'yxati chiqadi.</li>
          <li><b>Xizmatga o'tish</b> — kartochkadagi tugmalar: ruxsatnoma olish, auksion loti, ijara xizmati yoki shu joy haqida murojaat.</li>
          <li><b>Boshqaruv</b> — «+/−» masshtab, uy belgisi — umumiy ko'rinish, nishon — joylashuvingiz (ruxsat bilan), qatlam belgisi — sun'iy yo'ldosh tasviri, to'liq ekran.</li>
        </ol>
        <p className="hint">Xarita faqat ko'rish uchun. Ma'lumotlarni tahrirlash va tasdiqlash vakolatli xodimlar kabinetida bajariladi.</p>
      </Modal>
      {launcher.dialog}
    </section>
  );
}

